import { BigWin } from "@westpierstudio/core-module-oryx/components/bigwin";
import { SlotGameUIView } from "@westpierstudio/core-module-oryx/components/ui/slot-game-ui/SlotGameUIView";
import { Flow } from "@westpierstudio/luma/utils/flow";
import { FreeSpinCounter } from "components/FreeSpinCounter";
import { Reels } from "components/reels/Reels";
import { SpinType } from "components/reels/ReelsModel";
import { WinLines } from "components/winlines/WinLines";
import { CrossbowBonus } from "components/CrossbowBonus";
import { GameState, PrinceOfThievesGameModel } from "game/PrinceOfThievesGameModel";
import { PrinceOfThievesGameClient } from "game/PrinceOfThievesGameClient";
import { FreeSpinEvent } from "servertypes/gameevents/FreeSpin";
import { LastEvent, ReelSetSymbols } from "servertypes/SharedTypes";
import { Signal } from "type-signals";
import { PrinceOfThievesGameView } from "game/PrinceOfThievesGameView";
import { getSoundPlayer } from "sound/SoundLocator";
import { TotalWinPanel } from "components/TotalWinPanel";
import { RetriggerWinPanel } from "components/RetriggerWinPanel";
import { GameLogo } from "components/GameLogo";
import { GameEvent } from "servertypes/ServerEvents";
import { Background } from "components/Background";
import { Transition } from "components/Transition";
import { Jackpot } from "components/jackpot/Jackpot";
import { WIN_DURATIONS, getWinRollupDuration, getWinType, isBigWinType } from "@westpierstudio/core-module-oryx/utils/win-countup";
import { GameServices } from "game/GameServices";
import { PrinceOfThievesGameController } from "game/PrinceOfThievesGameController";
import { SignalBlockAllAction } from "game/actions/SignalBlockAllAction";

type Components = {
    freeSpinCounter: FreeSpinCounter,
    reels: Reels,
    crossbowBonus: CrossbowBonus,
    bigWin: BigWin,
    winlines: WinLines,
    totalWinPanel: TotalWinPanel,
    retriggerWinPanel: RetriggerWinPanel
    gameLogo: GameLogo,
    background: Background,
    transition: Transition,
    jackpot: Jackpot
}

export class FreeSpinEventHandler {
    signals = {
        reelSpin: new Signal<(spinResult: ReelSetSymbols, spinType: SpinType) => void>(),
        startBigWin: new Signal<(event: FreeSpinEvent) => void>(),
        showWinlines: new Signal<(event: FreeSpinEvent, spinType: SpinType) => void>(),
        updateBalance: new Signal<(event: FreeSpinEvent) => void>(),
        stopWinAnims: new Signal(),
        clearSkip: new Signal(),
        sessionComplete: new Signal<(event: FreeSpinEvent) => void>(),
        startShowPanel: new Signal(),
        startCrossbowBonus: new Signal(),
        startRetrigger: new Signal(),
        startSkippableDelay: new Signal<(time: number) => void>(),
        skippableDelayComplete: new Signal()
    };

    constructor(
        private readonly services: GameServices,
        private readonly controller: PrinceOfThievesGameController,
        private readonly model: PrinceOfThievesGameModel,
        private readonly view: PrinceOfThievesGameView,
        private readonly client: PrinceOfThievesGameClient,
        private readonly components: Components) {
    }

    run(flow: Flow, event: FreeSpinEvent, gameUI: SlotGameUIView): void {

        const { balance, balanceVersion } = event.body;
        const { betData, nextActions, accumulatedMoneyWon, totalMoneyWon } = event.body.data;
        const { bonusSymbolsPositions, multiplierData, freeSpinsCounter, reels: spinResult, startBonusData, paylineWins, finalSpinMoneyWon } = event.body.data.gameplay;

        const additionalFreeSpins = freeSpinsCounter.additionalFreeSpins;

        const { freeSpinCounter, reels, crossbowBonus, bigWin, winlines, totalWinPanel, retriggerWinPanel, gameLogo, background, transition, jackpot } = this.components;
        const spinType = this.model.getSpinType(true);
        const sound = getSoundPlayer();

        flow.reset();
        flow.addCallback(() => {
            reels.applyGamePadding(GameEvent.FREE_SPIN);
            background.setBackgroundFreeGame();
            freeSpinCounter.visible = true;
            crossbowBonus.visible = true;

            let total = freeSpinsCounter.freeSpinsTotal;
            let remaining = freeSpinsCounter.freeSpinsRemaining;

            if (additionalFreeSpins) {

                total -= additionalFreeSpins;
                remaining -= additionalFreeSpins;
            }

            freeSpinCounter.setFreeSpins(remaining, total);
        })
            .addCallback(() => this.signals.reelSpin.dispatch(spinResult, spinType))
            .addSignalBlock(reels.signals.spinComplete)
            .addCallback(() => this.signals.clearSkip.dispatch());

        if (multiplierData) {
            flow.addCallback(() => {
                crossbowBonus.startRevealMultipliers(multiplierData, this.model.reels.layout);
                this.signals.startCrossbowBonus.dispatch();
            })
                .addSignalBlock(crossbowBonus.signals.bonusComplete)
                .addCallback(() => this.signals.clearSkip.dispatch())
                .addCallback(() => getSoundPlayer().crossbowWin(accumulatedMoneyWon, betData.playerBet));
        }

        if (bonusSymbolsPositions) {

            flow.addCallback(() => {
                this.signals.startRetrigger.dispatch();
                winlines.showBonusWin(bonusSymbolsPositions);
                reels.startScatterWinAnimationSequences(bonusSymbolsPositions);
                sound.bonusBellRetrigger();
            })
                .addSignalBlock(sound.signals.bonusBellRetriggerComplete)
                .addCallback(() => this.signals.clearSkip.dispatch())
                .addCallback(() => winlines.reset());
        }

        if (bonusSymbolsPositions && bonusSymbolsPositions.length > 1) {
            flow.addCallback(() => {
                this.signals.startShowPanel.dispatch();
                retriggerWinPanel.show(additionalFreeSpins);
                freeSpinCounter.countUpToTotalFreeSpins(freeSpinsCounter.freeSpinsRemaining, freeSpinsCounter.freeSpinsTotal);
            })
                .addSignalBlock(retriggerWinPanel.signals.complete)
                .addCallback(() => this.signals.clearSkip.dispatch())
                .addCallback(() => {
                    freeSpinCounter.skip(freeSpinsCounter.freeSpinsTotal);
                    if (bonusSymbolsPositions) {
                        reels.bonusExtraFreeSpinsAnimations(bonusSymbolsPositions);
                    }
                });
        }

        if (this.model.winlines.lineCount > 0) {
            flow.addCallback(() => this.signals.showWinlines.dispatch(event, spinType));
            if (this.model.jackpot) {
                flow.addCallback(() => jackpot.startJackpot(paylineWins));
            }
            flow.addCallback(() => getSoundPlayer().startRollup(totalMoneyWon, betData.playerBet, true));
            if (this.model.winlines.winsContainBonus) {
                //if a win contains a retrigger bonus line use max winline time
                flow.addCallback(() => this.signals.startSkippableDelay.dispatch(WIN_DURATIONS.SMALL_WIN));
                if (totalMoneyWon > 0) {
                    flow.add(new SignalBlockAllAction(gameUI.signals.winRollupComplete, this.signals.skippableDelayComplete));
                }
                else {
                    flow.addSignalBlock(this.signals.skippableDelayComplete);
                }
            }
            else {
                //otherwise use normal time
                flow.addSignalBlock(gameUI.signals.winRollupComplete);
            }
            flow.addCallback(() => getSoundPlayer().stopCurrentRollup());
            flow.addCallback(() => this.signals.clearSkip.dispatch());
            if (this.model.jackpot) {
                flow.addCallback(() => jackpot.stopJackpot());
            }
            if (multiplierData) {
                flow.addCallback(() => crossbowBonus.resetTargetMultipliers());
            }
            flow.addCallback(() => this.signals.stopWinAnims.dispatch());
        }


        if (nextActions) {
            flow.addCallback(() => this.client.sendFreeSpinRequest(betData, this.model.freeRoundId));
        } else {
            if (finalSpinMoneyWon !== undefined) {
                flow.addCallback(() => {
                    this.signals.startShowPanel.dispatch();
                    totalWinPanel.show(finalSpinMoneyWon, betData.playerBet);
                })
                    .addSignalBlock(totalWinPanel.signals.complete)
                    .addCallback(() => {
                        this.signals.clearSkip.dispatch();
                    });
            }
            flow.addCallback(() => {

                const { bonusSymbolsPositions, reels: preBonusReels } = startBonusData;

                //start transition back to basegame
                getSoundPlayer().stopBonusMusic();
                transition.doTransitionToBase(() => {
                    background.setBackgroundBaseGame();
                    gameLogo.setSpinType(GameEvent.INITIAL_SPIN);
                    reels.applyGamePadding(GameEvent.INITIAL_SPIN);

                    freeSpinCounter.visible = false;
                    crossbowBonus.visible = false;

                    reels.setCurrentReelSymbols(preBonusReels);
                    reels.bonusCompleteAnimations(bonusSymbolsPositions);
                    winlines.showBonusWin(bonusSymbolsPositions, true, finalSpinMoneyWon);
                    reels.startScatterWinAnimationSequences(bonusSymbolsPositions);
                });
            });
            flow.addSignalBlock(transition.signals.fadeComplete);

            const winType = getWinType(betData.playerBet, finalSpinMoneyWon!);
            const isBigWin = isBigWinType(winType);

            if (isBigWin) {
                flow.addCallback(() => {
                    this.model.state = GameState.SHOW_BIG_WIN;
                })
                    .addCallback(() => this.signals.startBigWin.dispatch(event))
                    .addSignalBlock(bigWin.signals.complete);
            }
            flow.addCallback(() => {
                this.signals.updateBalance.dispatch(event);
                this.signals.sessionComplete.dispatch(event);
            });
        };

        flow.run();
    }

    resume(flow: Flow, lastEvent: LastEvent): void {

        const { betData } = lastEvent.data;
        const { reels } = this.components;

        flow.addCallback(() => {
            this.client.sendFreeSpinRequest(betData, this.model.freeRoundId);
        });
    }
}